import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getDirection } from "@/utils/rtl";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 print:hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  const { i18n } = useTranslation();
  const direction = getDirection(i18n.language);
  const isRTL = direction === 'rtl';
  
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        dir={direction}
        className={cn(
          "fixed top-[5%] z-50 grid w-full max-w-lg gap-4 border border-border/80 !bg-background p-6 shadow-2xl !rounded-2xl [&_input:not([type=checkbox]):not([type=radio])]:h-9 [&_input:not([type=checkbox]):not([type=radio])]:!bg-background [&_input:not([type=checkbox]):not([type=radio])]:text-xs [&_textarea]:!bg-background [&_textarea]:text-xs [&_label]:text-xs [&_label]:font-medium [&_[role=combobox]]:h-9 [&_[role=combobox]]:!bg-background [&_[role=combobox]]:text-xs duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          isRTL 
            ? "right-[50%] translate-x-[50%] data-[state=closed]:slide-out-to-right-1/2 data-[state=closed]:slide-out-to-top-[5%] data-[state=open]:slide-in-from-right-1/2 data-[state=open]:slide-in-from-top-[5%]"
            : "left-[50%] translate-x-[-50%] data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[5%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[5%]",
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className={cn(
          "absolute top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
          isRTL ? "left-4" : "right-4"
        )}>
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const { i18n } = useTranslation();
  const direction = getDirection(i18n.language);
  const isRTL = direction === 'rtl';
  
  return (
    <div
      className={cn(
        "flex flex-col space-y-1.5 text-center",
        isRTL ? "sm:text-right" : "sm:text-left",
        className
      )}
      {...props}
    />
  );
};
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const { i18n } = useTranslation();
  const direction = getDirection(i18n.language);
  const isRTL = direction === 'rtl';
  
  return (
    <div
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end",
        isRTL ? "sm:space-x-reverse sm:space-x-2" : "sm:space-x-2",
        className
      )}
      style={{ direction }}
      {...props}
    />
  );
};
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  const { i18n } = useTranslation();
  const direction = getDirection(i18n.language);
  const textAlign = direction === 'rtl' ? 'right' : 'left';
  
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        "text-lg font-semibold leading-none tracking-tight",
        className
      )}
      style={{ textAlign }}
      {...props}
    />
  );
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  const { i18n } = useTranslation();
  const direction = getDirection(i18n.language);
  const textAlign = direction === 'rtl' ? 'right' : 'left';
  
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-sm text-muted-foreground", className)}
      style={{ textAlign }}
      {...props}
    />
  );
});
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};

